'use client';

import { useState, useEffect, useCallback } from 'react';
import type { OpdsFeed, LibraryConnection } from '@/lib/opds/types';
import { fetchOpdsFeed } from '@/lib/utils/fetch';
import { useFeedTitles } from '@/stores/feed-titles';

export function useOpdsFeed(feedUrl: string | null, library: LibraryConnection | undefined) {
  const [feed, setFeed] = useState<OpdsFeed | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!feedUrl || !library) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchOpdsFeed(feedUrl, library);
      setFeed(result);
      // Populate the breadcrumb title cache so revisiting any ancestor
      // shows its localized title without an extra fetch.
      if (result?.title) useFeedTitles.getState().setTitle(feedUrl, result.title);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [feedUrl, library]);

  useEffect(() => {
    load();
  }, [load]);

  return { feed, loading, error, reload: load };
}
